import { getUniqueId } from 'common/utils/getUniqueId'

import i18n from 'i18n'

import { WEIGHT, REWARD_AVAILABILITY, MIN_REWARD_QUANTITY } from './components/Reward/constants'

import {
    defined,
    itemRules,
    minLength,
    maxLength,
    minNumber,
    oneOf,
    required,
    createValidator,
    createCollector,
    dependOn,
    isIntegerNumber,
} from 'shared/validation'

export class RewardModel {
    id // <String>
    previewImageUrl // <String[URL]/null>
    name // <String>
    weight // <String>
    availability // <String>
    quantity // <number/null>
    header // <String>
    description // <String>
    imageUrl // <String[URL]/null>
    imageDisclaimer // <String>
    buttonText // <String>
    buttonUrl // <String>

    constructor({
        previewImageUrl = 'https://p.interacty.me/rewards/reward1.png',
        name,
        weight = WEIGHT.NORMAL,
        availability = REWARD_AVAILABILITY.UNLIMITED,
        quantity = null,
        header = i18n.t('You’ve won a reward!'),
        description = i18n.t('Congratulations! You played well and earned one of our unique rewards.'),
        imageUrl = 'https://p.interacty.me/rewards/final-image.png',
        imageDisclaimer = '',
        buttonText = '',
        buttonUrl = '',
        index,
    } = {}) {
        this.id = getUniqueId()
        this.previewImageUrl = previewImageUrl
        this.name = name || i18n.t('Reward') + (typeof index !== 'undefined' ? ` ${index + 1}` : '')
        this.weight = weight
        this.availability = availability
        this.quantity = quantity
        this.header = header
        this.description = description
        this.imageUrl = imageUrl
        this.imageDisclaimer = imageDisclaimer
        this.buttonText = buttonText
        this.buttonUrl = buttonUrl
    }

    static get id() {
        return 'id'
    }

    static get previewImageUrl() {
        return 'previewImageUrl'
    }

    static get name() {
        return 'name'
    }

    static get weight() {
        return 'weight'
    }

    static get availability() {
        return 'availability'
    }

    static get quantity() {
        return 'quantity'
    }

    static get header() {
        return 'header'
    }

    static get description() {
        return 'description'
    }

    static get imageUrl() {
        return 'imageUrl'
    }

    static get imageDisclaimer() {
        return 'imageDisclaimer'
    }

    static get buttonText() {
        return 'buttonText'
    }

    static get buttonUrl() {
        return 'buttonUrl'
    }
}

function convertItemErrorsToMap(items, errors) {
    return new Map(items.map((item, index) => [item.id, errors[index]]))
}

const VALIDATION_RULES = {
    id: [required()],
    list: [
        itemRules(
            {
                [RewardModel.id]: [required()],
                [RewardModel.previewImageUrl]: [required()],
                [RewardModel.name]: [required()],
                [RewardModel.weight]: [required(), oneOf(Object.values(WEIGHT))],
                [RewardModel.availability]: [required(), oneOf(Object.values(REWARD_AVAILABILITY))],
                [RewardModel.quantity]: [
                    dependOn(({ self }) => {
                        return self[RewardModel.availability] === REWARD_AVAILABILITY.LIMITED ? required() : null
                    }),
                    dependOn(({ self }) => {
                        return self[RewardModel.availability] === REWARD_AVAILABILITY.LIMITED ? isIntegerNumber() : null
                    }),
                    dependOn(({ self }) => {
                        return self[RewardModel.availability] === REWARD_AVAILABILITY.LIMITED
                            ? minNumber(MIN_REWARD_QUANTITY)
                            : null
                    }),
                ],
                [RewardModel.header]: [required(), minLength(1), maxLength(500)],
                [RewardModel.description]: [maxLength(5000)],
                [RewardModel.imageUrl]: [required()],
                [RewardModel.imageDisclaimer]: [maxLength(100)],
                [RewardModel.buttonText]: [defined()],
                [RewardModel.buttonUrl]: [defined()],
            },
            convertItemErrorsToMap,
        ),
    ],
}

export const validate = data => ({
    data,
    isHasError: !createValidator(VALIDATION_RULES)(data),
    errors: createCollector(VALIDATION_RULES)(data),
})

export const getDefaultRewards = () => ({
    id: getUniqueId(),
    list: [0, 1, 2, 3].map(
        index =>
            new RewardModel({
                index,
                previewImageUrl: `https://p.interacty.me/rewards/reward${index + 1}.png`,
            }),
    ),
})
